/**
 * CopilotContext — global state for the ERP AI Copilot.
 * Mounted once at App level (above <Routes>) so the conversation, session id,
 * and open/closed state survive page navigations (individual pages remount
 * their own <Layout>, but this provider does not).
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { copilotApi } from '../services/copilotApi';

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const CopilotContext = createContext(null);

export const useCopilot = () => useContext(CopilotContext);

export const CopilotProvider = ({ children }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(uid);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);

  const send = useCallback(async (text) => {
    const msg = (text ?? '').trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { id: uid(), role: 'user', text: msg }]);
    setBusy(true);

    const asstId = uid();
    setMessages((m) => [...m, {
      id: asstId, role: 'assistant', text: '', actions: [], pending: [], charts: [],
      done: false, streaming: true,
    }]);
    const appendDelta = (chunk) => setMessages((m) => m.map(
      (x) => (x.id === asstId ? { ...x, text: (x.text || '') + chunk } : x)));

    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/copilot/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!resp.ok || !resp.body) throw new Error(`stream ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalActions = [];

      const handleEvent = (evt) => {
        if (evt.type === 'delta') {
          appendDelta(evt.text || '');
        } else if (evt.type === 'done') {
          finalActions = evt.actions || [];
          setMessages((m) => m.map((x) => (x.id === asstId ? {
            ...x, text: evt.reply ?? x.text, actions: evt.actions || [],
            pending: evt.pending_writes || [], charts: evt.charts || [],
            streaming: false,
          } : x)));
        } else if (evt.type === 'error') {
          appendDelta(evt.error ? `\n\n(${evt.error})` : '');
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          try { handleEvent(JSON.parse(line.slice(5).trim())); } catch { /* ignore */ }
        }
      }

      setMessages((m) => m.map((x) => (x.id === asstId ? { ...x, streaming: false } : x)));
      const nav = finalActions.find((a) => a.type === 'navigate');
      if (nav) setTimeout(() => navigate(nav.path), 400);
    } catch (e) {
      setMessages((m) => m.map((x) => (x.id === asstId ? {
        ...x, streaming: false,
        text: x.text || 'Sorry, something went wrong. Please try again.',
      } : x)));
    } finally {
      setBusy(false);
    }
  }, [busy, sessionId, navigate]);

  const resolvePending = useCallback(async (msgId, pend, confirm) => {
    setBusy(true);
    try {
      if (confirm) {
        const { data } = await copilotApi.confirm(sessionId, pend.pending_id);
        setMessages((m) => [
          ...m.map((x) => (x.id === msgId ? { ...x, done: true } : x)),
          { id: uid(), role: 'assistant', text: data.reply },
        ]);
        toast.success('Action applied');
        if (data.navigate) setTimeout(() => navigate(data.navigate), 500);
      } else {
        await copilotApi.cancel(sessionId, pend.pending_id);
        setMessages((m) => [
          ...m.map((x) => (x.id === msgId ? { ...x, done: true } : x)),
          { id: uid(), role: 'assistant', text: 'Okay, I cancelled that — nothing was changed.' },
        ]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not process the action');
    } finally {
      setBusy(false);
    }
  }, [sessionId]);

  const value = { open, setOpen, messages, busy, send, resolvePending, navigate };
  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
};
